import {
    eventRepository,
    playRepository,
    seasonRepository
} from "../storage/storage";
import SeasonService from "./season-service";
import ValidationService from "./validation-service";
import EventStatus, { EventStatusCode } from "../sports/common/event-status";
import GamedayApiService from "./gameday-api-service";
import ContextService from "./context-service";
import GameService from "./game-service";
import {
    applyConfigEdits,
    diffConfigEdits,
    mergeConfigSections,
    migrateClockConfig,
    normalizeFieldConfig,
    validateFieldConfig
} from "../sports/football/utils/field-config-utils";
import { areEqual } from "./common/object-util";

class EventService {
    async getGameConfigurationOrDefault(eventId, skipRemoteRefresh = false) {
        const event = await eventRepository.getById(eventId);
        const season = await seasonRepository.getBySeasonId(event.seasonId);
        const game = await GameService.getGameByEventId(eventId);
        const { ruleSetId } =
            (await ContextService.getGameContext(game.id)) || {};

        let { gameConfiguration, customFormData, rulesetDefaults } =
            await SeasonService.getGameConfigurationOrDefault(
                season.id,
                ruleSetId,
                skipRemoteRefresh
            );

        // Spread the event override over the season-resolved config (which
        // already carries the defaults), so keys missing from an older event
        // config fall back to the season value: event > season > defaults.
        const eventConfig = migrateClockConfig(event.gameConfiguration);
        gameConfiguration = mergeConfigSections(gameConfiguration, eventConfig);

        return { gameConfiguration, customFormData, rulesetDefaults };
    }

    async saveGameConfiguration(eventId, gameConfiguration) {
        const normalized = normalizeFieldConfig(gameConfiguration);
        validateFieldConfig(normalized.general);
        const changedOutsideClock = await this._validateSettingsEdit(
            eventId,
            normalized
        );

        const event = await eventRepository.getById(eventId);
        event.gameConfiguration = normalized;
        await eventRepository.put(event);

        const gameContext = await this._applyConfigurationToLiveGame(
            eventId,
            normalized,
            changedOutsideClock
        );

        return {
            ...(await this.getGameConfigurationOrDefault(eventId)),
            gameContext
        };
    }

    /**
     * True when the two configurations differ anywhere outside the clock
     * section. Deep value equality (key-order-insensitive): a stringify
     * compare falsely flagged unchanged configs whenever key order
     * differed — e.g. after migrateClockConfig rebuilds general via a
     * rest-spread — which tripped the after-start edit guard.
     */
    _changedOutsideClock(configA, configB) {
        const stripClock = ({ clock, ...sections }) => sections;
        return !areEqual(stripClock(configA), stripClock(configB));
    }

    /**
     * Enforce the after-start settings lock. Clock settings stay editable
     * once the game has started, so the guard only applies when something
     * OUTSIDE the clock section changed.
     * @returns {Promise<boolean>} whether non-clock fields changed
     */
    async _validateSettingsEdit(eventId, normalized) {
        const { gameConfiguration: current } =
            await this.getGameConfigurationOrDefault(eventId, true);
        const changedOutsideClock = this._changedOutsideClock(
            normalized,
            current
        );
        if (changedOutsideClock) {
            await ValidationService.canEditEventSettings(eventId);
        }
        return changedOutsideClock;
    }

    /**
     * Propagate a just-saved configuration to the live game, when there is
     * one. On a started game a clock-only change patches just the clock
     * section — a full gameConfig replace would clobber values that
     * setStartSettings resolved at game start and that only live on the
     * context (firstDownYardSet, teamZoneQty). The clock is then
     * unconditionally reconciled against the context's own config (the
     * post-start authority): a missing runningClock flag means manual,
     * never "leave whatever is there" — that no-op is how drifted
     * contexts used to stay stuck on the running clock. Sports without
     * clock settings (tennis) no-op inside the reconciler.
     * @returns {Promise<GameContext | null>} the updated context, if any
     */
    async _applyConfigurationToLiveGame(
        eventId,
        normalized,
        changedOutsideClock
    ) {
        const game = await GameService.getGameByEventId(eventId);
        const liveContext = game
            ? await ContextService.getGameContext(game.id)
            : null;

        if (!changedOutsideClock && liveContext?.isGameInitialized()) {
            liveContext.gameConfig = {
                ...liveContext.gameConfig,
                clock: { ...liveContext.gameConfig?.clock, ...normalized.clock }
            };
            await ContextService.updateContext(liveContext);
        } else {
            // TODO: Make sure this is the right place to do this.
            await this.updateGameContextByEventId(eventId, normalized);
        }

        let gameContext = null;
        if (game) {
            gameContext =
                (await ContextService.reconcileClock(game.id)) || null;
        }
        return gameContext;
    }

    /**
     * Update only the flag zone-to-gain spots (firstDownYardSet) on the event
     * configuration and propagate them to the live game. Intentionally skips
     * the canEditEventSettings guard so crews can adjust the spots from the
     * Update Field screen while a game is in progress. Marks the set as
     * crew-authored so startGame preserves it.
     * @param {string} eventId
     * @param {number[]} firstDownYardSet
     * @returns {Promise<{gameConfiguration, customFormData}>}
     */
    async saveFirstDownYardSet(eventId, firstDownYardSet) {
        const { gameConfiguration } = await this.getGameConfigurationOrDefault(
            eventId
        );
        const updated = {
            ...gameConfiguration,
            general: {
                ...gameConfiguration.general,
                firstDownYardSet,
                customFirstDownYardSet: true
            }
        };
        validateFieldConfig(updated.general);

        const event = await eventRepository.getById(eventId);
        event.gameConfiguration = updated;
        await eventRepository.put(event);

        await this.updateGameContextByEventId(eventId, updated);

        return this.getGameConfigurationOrDefault(eventId);
    }

    async setRuleSetId(eventId, ruleSetId) {
        await ValidationService.canEditEventSettings(eventId);

        const event = await eventRepository.getById(eventId);
        const season = await seasonRepository.getBySeasonId(event.seasonId);
        const game = await GameService.getGameByEventId(eventId);
        const liveContext = game
            ? await ContextService.getGameContext(game.id)
            : null;

        // Nothing to change, and this is not a free call to make anyway: the
        // rebuild below drops any setting the rule set does not declare, which
        // reprices already-recorded plays; it bumps settingsUpdatedAt, so the
        // remote push can clobber a newer save from another device; and it
        // rewrites every play's context snapshot.
        //
        // The season row and the game context are checked separately because
        // they CAN diverge - _doRefreshFromRemote moves the season's ruleSetId
        // without touching any game context - and skipping while they disagree
        // would leave the context and every play snapshot naming the old rule
        // set, a worse incoherence than the one this method prevents.
        //
        // Optional chaining rather than a null branch: canEditEventSettings
        // above already dereferences the context unguarded, so a missing one
        // throws before this runs. Written this way a context that somehow is
        // missing fails SAFE - the guard does not match, the full path runs,
        // and that path handles a null context on its own.
        if (
            season.ruleSetId === ruleSetId &&
            liveContext?.ruleSetId === ruleSetId
        ) {
            return {
                ...(await this.getGameConfigurationOrDefault(eventId)),
                gameContext: liveContext
            };
        }

        // The event's OWN edits, captured before the season row moves. The
        // settings form saves the whole resolved configuration, so an event
        // override is whatever differs from the season-resolved config it was
        // authored over - and once the rule set id changes, that baseline is
        // gone. Same ordering constraint that the season layer has.
        //
        // That baseline resolves through the GAME CONTEXT's rule set, not the
        // season's, because that is how getGameConfigurationOrDefault renders
        // the event form. The two agree except on a drifted season, and there
        // a season-side baseline mistakes every default that differs between
        // the two rule sets for a crew edit and carries it - Standard's
        // 100-yard field onto an Indoor event.
        const { gameConfiguration: previousResolved } =
            await SeasonService.getGameConfigurationOrDefault(
                season.id,
                liveContext?.ruleSetId,
                true
            );
        const eventEdits = diffConfigEdits(
            previousResolved,
            migrateClockConfig(event.gameConfiguration)
        );

        await SeasonService.applyRuleSetChange(season.id, ruleSetId);

        let updatedContext = null;

        if (game) {
            const gameContext = await ContextService.getGameContext(game.id);
            if (gameContext) {
                gameContext.ruleSetId = ruleSetId;
                updatedContext = await ContextService.updateContext(
                    gameContext
                );
            }

            //Update context snapshot of any existing plays;
            await this._updatePlayContextSnapshots(game, ruleSetId);
        }

        // Rebuild the event layer the same way: its edits over the newly
        // resolved season config, dropping anything the new rule set does not
        // declare. This replaces a call to restoreDefaultGameConfiguration,
        // which discarded every event-level setting the crew had chosen.
        const { gameConfiguration: nextResolved } =
            await SeasonService.getGameConfigurationOrDefault(
                season.id,
                ruleSetId,
                true
            );
        const saved = await this.saveGameConfiguration(
            eventId,
            applyConfigEdits(nextResolved, eventEdits)
        );

        return {
            ...saved,
            // The save re-read the context after writing the new config, so it
            // is the more current of the two; updatedContext only stands in
            // when there is no live game to apply anything to.
            gameContext: saved.gameContext || updatedContext
        };
    }

    async _updatePlayContextSnapshots(game, ruleSetId) {
        const gamePlays = await playRepository.getPlaysWithFragments(game.id);
        for (const play of gamePlays) {
            play.setContextSnapshot({
                ...play.getContextSnapshot(),
                ruleSetId
            });
            await playRepository.put(play);
        }
    }

    async updateGameContextByEventId(eventId, gameConfiguration) {
        const gameContext = await this.getGameContextByEventId(eventId);
        if (gameContext) {
            gameContext.gameConfig = gameConfiguration;
            await ContextService.updateContext(gameContext);
        }
    }

    async getGameContextByEventId(eventId) {
        const game = await GameService.getGameByEventId(eventId);
        if (game) {
            return await ContextService.getGameContext(game.id);
        }

        return null;
    }

    async restoreDefaultGameConfiguration(eventId) {
        const event = await eventRepository.getById(eventId);
        const season = await seasonRepository.getBySeasonId(event.seasonId);
        const game = await GameService.getGameByEventId(eventId);
        const { ruleSetId } =
            (await ContextService.getGameContext(game.id)) || {};

        const { gameConfiguration } =
            await SeasonService.getGameConfigurationOrDefault(
                season.id,
                ruleSetId
            );

        return this.saveGameConfiguration(eventId, gameConfiguration);
    }

    async getById(eventId) {
        return eventRepository.getById(eventId);
    }

    async setEventStatus(eventId, statusCode) {
        const event = await eventRepository.getById(eventId);
        event.statusCode = statusCode;
        event.status = EventStatus.resolve(event);
        await eventRepository.put(event);
        return eventRepository.getById(eventId);
    }

    async setEventStarted(eventId) {
        const event = await eventRepository.getById(eventId);
        event.score = { away: "0", home: "0" };
        event.statusCode = EventStatusCode.IN_PROGRESS;
        event.status = "";
        return await GamedayApiService.saveOrUpdateEvent(event);
    }

    async getByExternalId(externalEventId) {
        const event = await GamedayApiService.getEventByExternalId(
            externalEventId
        );
        await ContextService.updateContextScore(event);

        return event;
    }

    async setSelectedStreamId(eventId, streamId) {
        const event = await eventRepository.getById(eventId);
        event.selectedStreamId = streamId;
        await eventRepository.put(event);

        return event;
    }
}

export default new EventService();
